import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

// Minimum typed characters before the patient search API is called
const PATIENT_SEARCH_MIN_LENGTH = 2;
// Delay between keystrokes before triggering patient search
const PATIENT_SEARCH_DEBOUNCE_MS = 500;

/**
 * Debounced, abortable patient search against get_patient_list_with_aabha_info/.
 *
 * Extracted from patient-local-data so the navbar search and that screen share
 * one implementation instead of duplicating the debounce/abort plumbing.
 */
export default function usePatientSearch() {
  const [patientList, setPatientList] = useState([]);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const fetchPatientList = useCallback((searchQuery) => {
    if (abortRef.current) abortRef.current.abort();

    const abortController = new AbortController();
    abortRef.current = abortController;

    const authToken = sessionStorage.getItem("accessToken") || "";

    setLoading(true);
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { search: searchQuery },
        signal: abortController.signal,
      })
      .then((res) => {
        if (abortController.signal.aborted) return;
        setPatientList(res.data?.data || []);
      })
      .catch((err) => {
        if (
          axios.isCancel(err) ||
          err.name === "CanceledError" ||
          err.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("Failed to fetch patients:", err);
        toast.error("Failed to load patient list");
        setPatientList([]);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoading(false);
      });
  }, []);

  const reset = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
    setPatientList([]);
  }, []);

  /** Wire straight to an Autocomplete's onInputChange. */
  const onInputChange = useCallback(
    (event, newInputValue, reason) => {
      if (reason === "reset") return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      const trimmed = (newInputValue || "").trim();

      if (trimmed.length < PATIENT_SEARCH_MIN_LENGTH) {
        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
        setLoading(false);
        setPatientList([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetchPatientList(trimmed);
      }, PATIENT_SEARCH_DEBOUNCE_MS);
    },
    [fetchPatientList],
  );

  return { patientList, loading, onInputChange, reset };
}
